import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Search,
  ShieldCheck,
  Layers,
  Radio,
  FolderKanban,
  Group,
  BookOpen,
  BarChart3,
  Settings,
  Activity,
  Sliders,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ArrowRightLeft,
  Sparkles,
  Stethoscope,
  Boxes,
} from 'lucide-react';

type SimpleItem = { kind: 'link'; to: string; icon: any; label: string };
type GroupItem = {
  kind: 'group';
  key: string;                     // stable id for open/close state
  label: string;
  icon: any;
  // Children can themselves be groups (one extra level of nesting) — used
  // for the "Data Quality > DQ Control Plane" nested subgroup.
  children: Array<SimpleItem | GroupItem>;
};

// Navigation structure — three conceptual regions under Home:
//   1. Source Profiling — bringing data in (profile, map, onboard via agent)
//   2. Data Quality — running checks against onboarded data
//   3. Data Observability — monitoring + investigation
//
// Quality Reports sits separately at the bottom (cross-cutting view).
const navItems: Array<SimpleItem | GroupItem> = [
  { kind: 'link', to: '/', icon: LayoutDashboard, label: 'Home' },

  {
    kind: 'group',
    key: 'source-profiling',
    label: 'Source Profiling',
    icon: Search,
    children: [
      { kind: 'link', to: '/source-profiling',       icon: Search,         label: 'Data Profiling' },
      { kind: 'link', to: '/mapping',                icon: ArrowRightLeft, label: 'Schema Mapping' },
      { kind: 'link', to: '/source-profiling/agent', icon: Sparkles,       label: 'Profiling Agent' },
    ],
  },

  // M&A Pack — standalone building-block agents: Data Content Profiler
  // + Schema Mapping Agent (both live).
  {
    kind: 'group',
    key: 'ma-pack',
    label: 'M&A Pack',
    icon: Boxes,
    children: [
      { kind: 'link', to: '/data-content-profiler', icon: Sparkles,       label: 'Data Content Profiler' },
      { kind: 'link', to: '/schema-mapping-agent',   icon: ArrowRightLeft, label: 'Schema Mapping Agent' },
    ],
  },

  {
    kind: 'group',
    key: 'data-quality',
    label: 'Data Quality',
    icon: ShieldCheck,
    children: [
      { kind: 'link', to: '/sdq', icon: ShieldCheck, label: 'SDQ' },
      { kind: 'link', to: '/udq', icon: Layers,      label: 'UDQ' },
      { kind: 'link', to: '/pdq', icon: Radio,       label: 'PDQ' },
      {
        kind: 'group',
        key: 'dq-control-plane',
        label: 'DQ Control Plane',
        icon: Sliders,
        children: [
          { kind: 'link', to: '/projects', icon: FolderKanban, label: 'Projects' },
          { kind: 'link', to: '/groups',   icon: Group,        label: 'Groups' },
          { kind: 'link', to: '/rules',    icon: BookOpen,     label: 'Rule Catalog' },
        ],
      },
    ],
  },

  {
    kind: 'group',
    key: 'data-observability',
    label: 'Data Observability',
    icon: Activity,
    children: [
      { kind: 'link', to: '/observability',         icon: Activity,    label: 'Data Products' },
      { kind: 'link', to: '/observability/analyst', icon: Stethoscope, label: 'Incident Analyst' },
    ],
  },

  { kind: 'link', to: '/reports', icon: BarChart3, label: 'Quality Reports' },

  // Legacy /workbench route is intentionally NOT exposed in the sidebar.
  // It stays accessible by direct URL until the old code is removed, but
  // isn't promoted as a product surface anymore — users reach the new
  // agents via "Profiling Agent" (under Source Profiling) and "Incident
  // Analyst" (under Data Observability).
];

// Collect every GroupItem in the nav tree (depth-first) so we can track
// open/close state per group key regardless of nesting depth. Written as
// an array-returning function (not a generator with yield*) so it stays
// compatible with the project's default TS target without requiring
// --downlevelIteration.
function _allGroups(items: Array<SimpleItem | GroupItem>): GroupItem[] {
  const out: GroupItem[] = [];
  for (const item of items) {
    if (item.kind === 'group') {
      out.push(item);
      for (const g of _allGroups(item.children)) out.push(g);
    }
  }
  return out;
}

// Is ANY descendant link (recursively) the current route?
function _anyDescendantActive(group: GroupItem, pathname: string): boolean {
  return group.children.some((c) =>
    c.kind === 'link'
      ? pathname.startsWith(c.to)
      : _anyDescendantActive(c, pathname),
  );
}

const Sidebar: React.FC = () => {
  const location = useLocation();

  // Collapsed = icon-only rail. Persisted so it survives navigation
  // and reloads.
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem('cargo-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    try {
      localStorage.setItem('cargo-sidebar-collapsed', v ? '1' : '0');
    } catch {
      /* ignore — non-persistent is fine */
    }
  };

  // Each group tracks its own open/close state by key. A group auto-opens
  // when the current route matches one of its descendant links — so
  // deep-linking to e.g. /rules expands BOTH "Data Quality" and the
  // nested "DQ Control Plane" subgroup.
  const initialOpen = Object.fromEntries(
    _allGroups(navItems).map((g) => [
      g.key,
      _anyDescendantActive(g, location.pathname),
    ]),
  ) as Record<string, boolean>;

  const [open, setOpen] = useState<Record<string, boolean>>(initialOpen);

  React.useEffect(() => {
    setOpen((prev) => {
      const next = { ...prev };
      for (const g of _allGroups(navItems)) {
        if (_anyDescendantActive(g, location.pathname)) next[g.key] = true;
      }
      return next;
    });
  }, [location.pathname]);

  // No `whitespace-nowrap` → long labels (e.g. "Schema Mapping Agent")
  // wrap to a second line instead of being clipped. Collapsed → icons
  // are centered (no labels).
  const linkClass = (isActive: boolean) =>
    `flex items-center ${collapsed ? 'justify-center px-2' : 'gap-3 px-3'} py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
      isActive
        ? 'bg-white/20 text-white shadow-sm'
        : 'text-white/60 hover:text-white hover:bg-white/10'
    }`;

  // Recursive renderer — handles links OR groups at any depth.
  const renderItem = (item: SimpleItem | GroupItem, depth = 0): React.ReactElement => {
    if (item.kind === 'link') {
      const Icon = item.icon;
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            linkClass(isActive) + (depth > 0 && !collapsed ? ' py-2' : '')
          }
        >
          <Icon className={(depth > 0 ? 'w-4 h-4' : 'w-5 h-5') + ' flex-shrink-0'} />
          {!collapsed && (
            <span className="flex-1 min-w-0 leading-tight break-words">{item.label}</span>
          )}
        </NavLink>
      );
    }
    const GroupIcon = item.icon;
    const isActive = _anyDescendantActive(item, location.pathname);
    const isOpen = open[item.key] ?? false;
    // Collapsed: clicking a group expands the sidebar AND opens that
    // group (children can't render in the narrow rail).
    const onGroupClick = () => {
      if (collapsed) {
        setCollapsed(false);
        setOpen((o) => ({ ...o, [item.key]: true }));
      } else {
        setOpen((o) => ({ ...o, [item.key]: !o[item.key] }));
      }
    };
    return (
      <div key={item.key}>
        <button
          onClick={onGroupClick}
          title={collapsed ? item.label : undefined}
          className={
            linkClass(isActive) + (collapsed ? '' : ' w-full justify-between')
            + (depth > 0 && !collapsed ? ' py-2' : '')
          }
        >
          <span className={`flex items-center ${collapsed ? '' : 'gap-3 min-w-0'}`}>
            <GroupIcon className={(depth > 0 ? 'w-4 h-4' : 'w-5 h-5') + ' flex-shrink-0'} />
            {!collapsed && (
              <span className="leading-tight break-words text-left">{item.label}</span>
            )}
          </span>
          {!collapsed && (
            <ChevronDown
              className={`${depth > 0 ? 'w-3.5 h-3.5' : 'w-4 h-4'} flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>
        {!collapsed && isOpen && (
          <div className="mt-1 ml-3 pl-3 border-l border-white/15 space-y-0.5">
            {item.children.map((c) => renderItem(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-60'} bg-[#1C1C46] border-r border-[#1C1C46] flex flex-col h-screen sticky top-0 transition-[width] duration-200`}
    >
      {/* Logo + collapse toggle */}
      <div
        className={`h-16 flex items-center border-b border-white/15 ${
          collapsed ? 'justify-center px-2' : 'px-5'
        }`}
      >
        {!collapsed && (
          <div className="flex-1 min-w-0">
            {/* Official WHITE Elevatenow logo (mark + wordmark) from
                elevatenow.tech — already white, so it sits directly on
                the blue sidebar (no pill, no separate text). */}
            <img
              src="/elevatenow-logo-white.svg"
              alt="Elevatenow"
              className="h-6 w-auto"
            />
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/15 transition-colors"
        >
          {collapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav
        className="flex-1 py-4 px-3 space-y-1 overflow-y-auto [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {navItems.map((item) => renderItem(item, 0))}
      </nav>

      {/* Bottom settings */}
      <div className="px-3 py-4 border-t border-white/15">
        <NavLink
          to="/settings"
          title={collapsed ? 'Settings' : undefined}
          className={({ isActive }) => linkClass(isActive)}
        >
          <Settings className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="flex-1 min-w-0 leading-tight">Settings</span>}
        </NavLink>
      </div>
    </aside>
  );
};

export default Sidebar;
